import { ArrowUpRight, Check, FileText, Mail, MessageSquareText, Paperclip, Phone, RotateCcw, ShieldCheck, Trash2, UploadCloud } from "lucide-react";
import React, { DragEvent, FormEvent, useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { CallbackScheduler } from "@/components/CallbackScheduler";
import { StructuredData } from "@/components/StructuredData";
import { TurnstileWidget } from "@/components/TurnstileWidget";
import { siteConfig } from "@/data/site";
import { MATERIAL_PREFERENCES, PROJECT_TYPES, type MaterialPreference, type ProjectType } from "@shared/quote";
import { formatFileSize, QUOTE_ATTACHMENT_ACCEPT, QUOTE_ATTACHMENT_ACCEPT_ATTRIBUTE, QUOTE_ATTACHMENT_LIMITS } from "@shared/quoteAttachments";
import { SERVICE_AREA_STATE_BY_SLUG, US_STATES, type UsStateCode } from "@shared/usStates";
import { createConversionAttribution } from "@/lib/attribution";
import { consumeAccessoryQuoteHandoff } from "@/lib/accessoryQuoteCart";
import { appendBulkModelReferences, BULK_MODEL_INPUT_LIMIT, getBulkModelReferenceValidationError, parseBulkModelReferences } from "@/lib/bulkModelReferences";
import { trackGa4Lead } from "@/lib/ga4";
import { consumePartitionBudgetQuoteHandoff, getPartitionBudgetQuoteMaterialPreference } from "@/lib/partitionBudgetQuote";
import { trpc } from "@/lib/trpc";
import type { AccessoryQuoteSelection } from "@shared/accessoryQuote";

type QuoteDraft = {
  name: string;
  email: string;
  phone: string;
  projectZip: string;
  projectCity: string;
  projectState: UsStateCode | "";
  projectType: ProjectType | "";
  materialPreference: MaterialPreference | "";
  quantity: string;
  message: string;
  company: string;
};

type QuoteResponse = { success: true; submissionId: number | null; accessoryPdfIncluded?: boolean };
type QuoteSubmissionSnapshot = { draft: QuoteDraft; files: File[]; accessorySelection: AccessoryQuoteSelection };

export const initialQuoteDraft: QuoteDraft = {
  name: "",
  email: "",
  phone: "",
  projectZip: "",
  projectCity: "",
  projectState: "",
  projectType: "",
  materialPreference: "",
  quantity: "",
  message: "",
  company: "",
};

export function createQuoteSubmissionDraft(draft: QuoteDraft): QuoteDraft {
  return {
    ...draft,
    projectType: draft.projectType || "Other commercial project",
    materialPreference: draft.materialPreference || "Not sure — help me compare",
    quantity: draft.quantity.trim() || "Not provided",
    message: draft.message.trim() || "No additional project details provided.",
  };
}

const usefulInputs = [
  "Plans, sketches, or rough dimensions",
  "Compartment and door quantities",
  "Material or finish direction",
  "Accessories and delivery location",
];

function getServiceAreaProjectState() {
  const serviceArea = new URLSearchParams(window.location.search).get("serviceArea");
  if (!serviceArea || !Object.prototype.hasOwnProperty.call(SERVICE_AREA_STATE_BY_SLUG, serviceArea)) return "";
  return SERVICE_AREA_STATE_BY_SLUG[serviceArea as keyof typeof SERVICE_AREA_STATE_BY_SLUG];
}

const ZIP_CODE_PATTERN = /^\d{5}(?:-\d{4})?$/;

export function normalizeProjectZip(value: string) {
  const digits = value.replace(/\D/g, "").slice(0, 9);
  return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
}

function sendMultipartQuote(formData: FormData, onProgress: (progress: number) => void) {
  return new Promise<QuoteResponse>((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("POST", "/api/quotes");
    request.withCredentials = true;
    request.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
    });
    request.addEventListener("load", () => {
      let result: QuoteResponse | { error?: string } = {};
      try {
        result = JSON.parse(request.responseText) as QuoteResponse | { error?: string };
      } catch {
        reject(new Error("The server returned an unexpected response. Please call 646-349-4040."));
        return;
      }
      if (request.status >= 200 && request.status < 300 && "success" in result) {
        resolve(result);
        return;
      }
      reject(new Error("error" in result && result.error ? result.error : "We could not submit the request. Please call 646-349-4040."));
    });
    request.addEventListener("error", () => reject(new Error("The request could not reach the server. Please check your connection and try again.")));
    request.addEventListener("abort", () => reject(new Error("The request was cancelled before it finished.")));
    request.send(formData);
  });
}

export function QuoteSubmissionSuccess({
  submissionId,
  notice,
  attachmentCount,
  onReset,
}: {
  submissionId: number | null;
  notice: string;
  attachmentCount: number;
  onReset: () => void;
}) {
  const sectionRef = useRef<HTMLElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true });
    const timeout = window.setTimeout(() => {
      sectionRef.current?.scrollIntoView({ behavior: "auto", block: "start" });
    }, 100);
    return () => window.clearTimeout(timeout);
  }, []);

  return (
    <section ref={sectionRef} className="quote-success is-visible" aria-labelledby="quote-success-title">
      <p className="sr-only" role="status" aria-live="polite">Your Partition Stalls quote request was submitted successfully.</p>
      <div className="quote-success__message">
        <div className="quote-success__eyebrow"><span><Check aria-hidden="true" /> Quote request received</span>{submissionId ? <strong>Request #{submissionId}</strong> : null}</div>
        <h2 id="quote-success-title" ref={headingRef} tabIndex={-1}>Thank you.<br />Your quote is in.</h2>
        <p>{notice}</p>
        <p className="quote-success__attachment-status">{attachmentCount ? `${attachmentCount} project ${attachmentCount === 1 ? "file was" : "files were"} included with this request.` : "No project files were attached to this request."}</p>
      </div>
      <div className="quote-success__contact">
        <span className="kicker kicker--light">Need to follow up?</span>
        <p>If you have not heard from us within 24 hours, please call or text.</p>
        <a className="quote-success__number" href={`tel:${siteConfig.phoneHref}`} aria-label={`Call Partition Stalls at ${siteConfig.phoneDisplay}`}>{siteConfig.phoneDisplay}</a>
        <div className="quote-success__contact-actions">
          <a href={`tel:${siteConfig.phoneHref}`} className="button button--primary"><Phone aria-hidden="true" /> Call now</a>
          <a href={`sms:${siteConfig.phoneHref}`} className="button button--outline" aria-label={`Text Partition Stalls at ${siteConfig.phoneDisplay}`}><MessageSquareText aria-hidden="true" /> Text us</a>
        </div>
      </div>
      <button type="button" className="quote-success__reset" onClick={onReset}><RotateCcw aria-hidden="true" /> Submit another quote</button>
    </section>
  );
}

export default function RequestQuote() {
  const [draft, setDraft] = useState<QuoteDraft>(initialQuoteDraft);
  const trpcUtils = trpc.useUtils();
  const [zipLookupMessage, setZipLookupMessage] = useState("");
  const [isLookingUpZip, setIsLookingUpZip] = useState(false);
  const [bulkModelReferences, setBulkModelReferences] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [accessorySelection, setAccessorySelection] = useState<AccessoryQuoteSelection>([]);
  const [dragActive, setDragActive] = useState(false);
  const [notice, setNotice] = useState("");
  const [submissionId, setSubmissionId] = useState<number | null>(null);
  const [submittedAttachmentCount, setSubmittedAttachmentCount] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [turnstileResetSignal, setTurnstileResetSignal] = useState(0);
  const [turnstileExecuteSignal, setTurnstileExecuteSignal] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const verificationRef = useRef<HTMLDivElement>(null);
  const verificationInFlightRef = useRef(false);
  const submissionInFlightRef = useRef(false);
  const pendingSubmissionRef = useRef<QuoteSubmissionSnapshot | null>(null);
  const zipLookupRequestRef = useRef(0);
  const locationFieldEditRef = useRef(0);
  const parsedBulkReferences = parseBulkModelReferences(bulkModelReferences);
  const bulkModelError = getBulkModelReferenceValidationError(draft.message, bulkModelReferences);

  useEffect(() => {
    const projectState = getServiceAreaProjectState();
    const linkedModels = new URLSearchParams(window.location.search).get("models")?.trim() ?? "";
    if (linkedModels) setBulkModelReferences(linkedModels);
    const accessoryHandoff = consumeAccessoryQuoteHandoff();
    if (accessoryHandoff) {
      setAccessorySelection(accessoryHandoff.lines.map(({ id, quantity }) => ({ id, quantity })));
      setDraft((current) => ({
        ...current,
        projectState: current.projectState || projectState,
        projectType: current.projectType || "Accessories only",
        materialPreference: current.materialPreference || "Accessories only",
        quantity: current.quantity || accessoryHandoff.quantity,
        message: current.message || accessoryHandoff.message,
      }));
      setNotice(`Your accessory quote list is ready. ${accessoryHandoff.itemCount} selected item${accessoryHandoff.itemCount === 1 ? "" : "s"} and available reference-price details were added to the project details. When you submit, a PDF of your selected list is included with the quote request.`);
      return;
    }
    const partitionBudgetHandoff = consumePartitionBudgetQuoteHandoff();
    if (partitionBudgetHandoff) {
      setDraft((current) => ({
        ...current,
        projectState: current.projectState || projectState,
        projectType: current.projectType || "Other commercial project",
        materialPreference: current.materialPreference || getPartitionBudgetQuoteMaterialPreference(partitionBudgetHandoff.configuration),
        quantity: current.quantity || partitionBudgetHandoff.quantity,
        message: current.message || partitionBudgetHandoff.message,
      }));
      setNotice("Your partition budget selection is ready. The planning range and selected project details were added to the project details.");
      return;
    }
    if (projectState) setDraft((current) => current.projectState ? current : { ...current, projectState });
  }, []);

  const updateField = <K extends keyof QuoteDraft>(field: K, value: QuoteDraft[K]) => setDraft((current) => ({ ...current, [field]: value }));

  const lookUpProjectZip = async (zip: string) => {
    const requestId = ++zipLookupRequestRef.current;
    const locationEditCount = locationFieldEditRef.current;
    if (!ZIP_CODE_PATTERN.test(zip)) {
      setIsLookingUpZip(false);
      setZipLookupMessage("");
      return;
    }

    setIsLookingUpZip(true);
    setZipLookupMessage("Finding city and state…");
    try {
      const location = await trpcUtils.quotes.projectLocation.fetch({ zip });
      if (requestId !== zipLookupRequestRef.current) return;
      if (!location) {
        setZipLookupMessage("City and state could not be filled. Please enter them below.");
        return;
      }
      if (locationEditCount !== locationFieldEditRef.current) {
        setZipLookupMessage("ZIP found. Your city and state entries were kept.");
        return;
      }
      setDraft((current) => ({ ...current, projectCity: location.city, projectState: location.state as UsStateCode }));
      setZipLookupMessage(`${location.city}, ${location.state} added from ZIP.`);
    } catch {
      if (requestId === zipLookupRequestRef.current) {
        setZipLookupMessage("City and state could not be filled. Please enter them below.");
      }
    } finally {
      if (requestId === zipLookupRequestRef.current) setIsLookingUpZip(false);
    }
  };

  const handleProjectZipChange = (value: string) => {
    const projectZip = normalizeProjectZip(value);
    updateField("projectZip", projectZip);
    void lookUpProjectZip(projectZip);
  };

  const addFiles = (incoming: File[]) => {
    const combined = [...files];
    for (const file of incoming) {
      if (!combined.some((current) => current.name === file.name && current.size === file.size && current.lastModified === file.lastModified)) combined.push(file);
    }
    if (combined.length > QUOTE_ATTACHMENT_LIMITS.maxFiles) {
      setNotice(`Attach no more than ${QUOTE_ATTACHMENT_LIMITS.maxFiles} files.`);
      return;
    }
    const invalidExtension = combined.find((file) => !QUOTE_ATTACHMENT_ACCEPT.some((extension) => file.name.toLowerCase().endsWith(extension)));
    if (invalidExtension) {
      setNotice(`${invalidExtension.name} is not an accepted PDF, image, DOCX, or XLSX file.`);
      return;
    }
    const oversized = combined.find((file) => file.size > QUOTE_ATTACHMENT_LIMITS.maxFileBytes);
    if (oversized) {
      setNotice(`${oversized.name} exceeds the 10 MB per-file limit.`);
      return;
    }
    const totalBytes = combined.reduce((sum, file) => sum + file.size, 0);
    if (totalBytes > QUOTE_ATTACHMENT_LIMITS.maxTotalBytes) {
      setNotice("Attachments must total 25 MB or less.");
      return;
    }
    setFiles(combined);
    setNotice("");
    setSubmissionId(null);
    setSubmitted(false);
  };

  const handleDrop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setDragActive(false);
    addFiles(Array.from(event.dataTransfer.files));
  };

  const removeFile = (index: number) => {
    setFiles((current) => current.filter((_, currentIndex) => currentIndex !== index));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const submitVerifiedQuote = async (token: string, submission: QuoteSubmissionSnapshot) => {
    const formData = new FormData();
    const submissionPayload = { ...submission.draft, ...createConversionAttribution(window.location.pathname) };
    Object.entries(submissionPayload).forEach(([key, value]) => {
      if (typeof value === "string" && value) formData.append(key, value);
    });
    formData.append("turnstileToken", token);
    if (submission.accessorySelection.length) formData.append("accessorySelection", JSON.stringify(submission.accessorySelection));
    submission.files.forEach((file) => formData.append("attachments", file, file.name));

    setIsSubmitting(true);
    setUploadProgress(0);
    try {
      const result = await sendMultipartQuote(formData, setUploadProgress);
      setSubmissionId(result.submissionId);
      setSubmittedAttachmentCount(submission.files.length + (result.accessoryPdfIncluded ? 1 : 0));
      setSubmitted(true);
      trackGa4Lead("quote_request");
      if (result.submissionId) {
        setNotice("Your quote request has been recorded and sent to info@partitionstalls.com. The Partition Stalls team will follow up using the contact details you provided.");
      } else {
        setNotice("Thank you. Your request has been received.");
      }
      setDraft({ ...initialQuoteDraft, projectState: getServiceAreaProjectState() });
      setBulkModelReferences("");
      setFiles([]);
      setAccessorySelection([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      setSubmitted(false);
      setNotice(error instanceof Error ? error.message : "We could not submit the request. Please call 646-349-4040 for immediate assistance.");
    } finally {
      submissionInFlightRef.current = false;
      pendingSubmissionRef.current = null;
      setIsSubmitting(false);
      setIsVerifying(false);
      setUploadProgress(0);
      setTurnstileResetSignal((current) => current + 1);
    }
  };

  const handleVerifiedToken = (token: string) => {
    if (!token) return;
    if (!verificationInFlightRef.current || submissionInFlightRef.current) return;
    const submission = pendingSubmissionRef.current;
    if (!submission) return;
    verificationInFlightRef.current = false;
    submissionInFlightRef.current = true;
    setIsVerifying(false);
    setNotice("Human verification complete. Sending your quote request…");
    void submitVerifiedQuote(token, submission);
  };

  const handleVerificationError = (message: string) => {
    if (submissionInFlightRef.current) return;
    verificationInFlightRef.current = false;
    pendingSubmissionRef.current = null;
    setIsVerifying(false);
    setNotice(message);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setNotice("");
    setSubmissionId(null);
    setSubmitted(false);
    if (isSubmitting || isVerifying) return;

    if (bulkModelError) {
      setNotice(bulkModelError);
      return;
    }
    if (draft.projectZip && !ZIP_CODE_PATTERN.test(draft.projectZip)) {
      setNotice("Enter a valid 5-digit U.S. ZIP code, or leave the ZIP field blank.");
      return;
    }
    pendingSubmissionRef.current = {
      draft: createQuoteSubmissionDraft({ ...draft, message: appendBulkModelReferences(draft.message, parsedBulkReferences) }),
      files: [...files],
      accessorySelection: accessorySelection.map((line) => ({ ...line })),
    };
    verificationInFlightRef.current = true;
    setIsVerifying(true);
    setNotice("Checking your submission…");
    setTurnstileExecuteSignal((current) => current + 1);
    window.requestAnimationFrame(() => verificationRef.current?.focus());
  };

  const resetQuoteForm = () => {
    setSubmitted(false);
    setSubmissionId(null);
    setSubmittedAttachmentCount(0);
    setNotice("");
    setDraft({ ...initialQuoteDraft, projectState: getServiceAreaProjectState() });
    setBulkModelReferences("");
    setFiles([]);
    setAccessorySelection([]);
    verificationInFlightRef.current = false;
    submissionInFlightRef.current = false;
    pendingSubmissionRef.current = null;
    setIsVerifying(false);
    setTurnstileResetSignal((current) => current + 1);
  };

  const contactPageSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "Request a Commercial Bathroom Partition Quote",
    url: `${siteConfig.url}/request-a-quote`,
    description: "Request pricing for commercial bathroom partitions, toilet stalls, and restroom accessories from Partition Stalls.",
    about: { "@id": `${siteConfig.url}/#business` },
  };

  return (
    <>
      <StructuredData data={contactPageSchema} />
      <section className={`quote-workspace section-line${submitted ? " quote-workspace--success" : ""}`} aria-labelledby={submitted ? "quote-success-title" : "quote-form-title"}>
        {!submitted && <header className="quote-guidance">
          <p className="breadcrumb breadcrumb--light"><Link href="/">Home</Link><span>/</span> Request a quote</p>
          <span className="kicker kicker--light">Start with what you know</span>
          <h1>Share the commercial restroom scope you have.</h1>
          <p>Exact specifications are not required to begin. Send the known project details and we will identify the open questions with you.</p>
          <ul aria-label="Helpful quote inputs">
            {usefulInputs.map((input) => <li key={input}><Check aria-hidden="true" />{input}</li>)}
          </ul>
        </header>}

        <div className="quote-workspace__form">
          {submitted ? (
            <QuoteSubmissionSuccess submissionId={submissionId} notice={notice} attachmentCount={submittedAttachmentCount} onReset={resetQuoteForm} />
          ) : (
            <>
              <div className="quote-form__heading">
                <div><span className="kicker">Project inquiry</span><h2 id="quote-form-title">Tell us about your project.</h2></div>
                <p>Start with your contact and project location. Add plans or details only if they are useful.</p>
              </div>
              <form className="quote-form" onSubmit={handleSubmit} encType="multipart/form-data">
                <fieldset className="quote-form__fields" aria-label="Quote request details" disabled={isSubmitting || isVerifying}>
                  <div className="form-grid form-grid--essentials">
                    <div className="form-field"><label htmlFor="name">Name</label><input id="name" name="name" value={draft.name} onChange={(event) => updateField("name", event.target.value)} autoComplete="name" required /></div>
                    <div className="form-field"><label htmlFor="email">Email</label><input id="email" name="email" type="email" value={draft.email} onChange={(event) => updateField("email", event.target.value)} autoComplete="email" required /></div>
                    <div className="form-field"><label htmlFor="phone">Phone</label><input id="phone" name="phone" type="tel" value={draft.phone} onChange={(event) => updateField("phone", event.target.value)} autoComplete="tel" required /></div>
                    <div className="form-field form-field--zip"><label htmlFor="projectZip">Project ZIP <small>Optional</small></label><input id="projectZip" name="projectZip" value={draft.projectZip} onChange={(event) => handleProjectZipChange(event.target.value)} autoComplete="postal-code" inputMode="numeric" maxLength={10} placeholder="e.g., 10001" aria-describedby="project-zip-help" />{(isLookingUpZip || zipLookupMessage) && <p id="project-zip-help" className={`project-zip-status${zipLookupMessage.includes("could not") ? " is-unresolved" : ""}`} aria-live="polite">{isLookingUpZip ? "Finding city and state…" : zipLookupMessage}</p>}</div>
                    <div className="form-field"><label htmlFor="projectCity">Project city</label><input id="projectCity" name="projectCity" value={draft.projectCity} onChange={(event) => { locationFieldEditRef.current += 1; updateField("projectCity", event.target.value); }} autoComplete="address-level2" placeholder="City" required /></div>
                    <div className="form-field"><label htmlFor="projectState">Project state</label><select id="projectState" name="projectState" value={draft.projectState} onChange={(event) => { locationFieldEditRef.current += 1; updateField("projectState", event.target.value as QuoteDraft["projectState"]); }} autoComplete="address-level1" required><option value="" disabled>Select state</option>{US_STATES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}</select></div>
                  </div>

                  <div className="form-field form-field--attachments quote-form__upload">
                    <div className="attachment-heading"><div><span>Plans or drawings</span><small>Optional</small></div><strong>{files.length}/{QUOTE_ATTACHMENT_LIMITS.maxFiles}</strong></div>
                    <label
                      className={`file-drop${dragActive ? " is-dragging" : ""}`}
                      htmlFor="quote-attachments"
                      onDragEnter={(event) => { event.preventDefault(); setDragActive(true); }}
                      onDragOver={(event) => { event.preventDefault(); setDragActive(true); }}
                      onDragLeave={(event) => { event.preventDefault(); setDragActive(false); }}
                      onDrop={handleDrop}
                    >
                      <UploadCloud aria-hidden="true" />
                      <span><strong>Upload plans, drawings, or project photos</strong><small>These can help us prepare the quote.</small></span>
                      <span className="file-drop__action"><Paperclip aria-hidden="true" />Choose files</span>
                      <input ref={fileInputRef} id="quote-attachments" name="attachments" type="file" multiple accept={QUOTE_ATTACHMENT_ACCEPT_ATTRIBUTE} onChange={(event) => addFiles(Array.from(event.target.files ?? []))} aria-describedby="attachment-help" />
                    </label>
                    <p id="attachment-help" className="attachment-help">PDF, PNG, JPG, WebP, DOCX, or XLSX. Up to 10 MB each and 25 MB total. Files are shared only with the Partition Stalls team.</p>
                    {files.length > 0 && <ul className="attachment-list" aria-label="Selected attachments">{files.map((file, index) => <li key={`${file.name}-${file.size}-${file.lastModified}`}><FileText aria-hidden="true" /><span><strong>{file.name}</strong><small>{formatFileSize(file.size)}</small></span><button type="button" onClick={() => removeFile(index)} aria-label={`Remove ${file.name}`}><Trash2 aria-hidden="true" /></button></li>)}</ul>}
                  </div>

                  <details className="quote-form__optional quote-form__optional--project">
                    <summary><span>Add project details</span><small>Optional</small></summary>
                    <div className="quote-form__optional-body">
                      <div className="form-field quote-form__bulk-models">
                        <label htmlFor="bulk-model-references">Bulk model or SKU list <small>Optional</small></label>
                        <textarea id="bulk-model-references" value={bulkModelReferences} onChange={(event) => setBulkModelReferences(event.target.value)} rows={3} maxLength={BULK_MODEL_INPUT_LIMIT} aria-describedby="bulk-model-help" placeholder={"Paste one model per line, or separate with commas.\nExample: Bobrick B-165 2436, ASI 0620-2436"} />
                        <p id="bulk-model-help">{bulkModelError ? bulkModelError : bulkModelReferences.trim() ? `${parsedBulkReferences.length} model reference${parsedBulkReferences.length === 1 ? "" : "s"} will be sent with the quote request.` : "Add model references for fast B2B quote preparation. Exact model configuration is confirmed in the written quote."}</p>
                      </div>

                      <div className="form-grid form-grid--optional">
                        <div className="form-field"><label htmlFor="projectType">Project type <small>Optional</small></label><select id="projectType" name="projectType" value={draft.projectType} onChange={(event) => updateField("projectType", event.target.value as QuoteDraft["projectType"])}><option value="">Not sure yet</option>{PROJECT_TYPES.map((projectType) => <option key={projectType}>{projectType}</option>)}</select></div>
                        <div className="form-field"><label htmlFor="materialPreference">Material preference <small>Optional</small></label><select id="materialPreference" name="materialPreference" value={draft.materialPreference} onChange={(event) => updateField("materialPreference", event.target.value as QuoteDraft["materialPreference"])}><option value="">Help me compare</option>{MATERIAL_PREFERENCES.map((material) => <option key={material}>{material}</option>)}</select></div>
                        <div className="form-field form-field--full"><label htmlFor="quantity">Quantity <small>Optional</small></label><input id="quantity" name="quantity" value={draft.quantity} onChange={(event) => updateField("quantity", event.target.value)} inputMode="numeric" placeholder="e.g., 12 compartments" /></div>
                        <div className="form-field form-field--full"><label htmlFor="message">Project details <small>Optional</small></label><textarea id="message" name="message" value={draft.message} onChange={(event) => updateField("message", event.target.value)} rows={4} placeholder="Facility type, layout, mounting style, finishes, accessories, schedule, delivery constraints, or questions" /></div>
                      </div>
                    </div>
                  </details>

                  <div className="form-field form-field--honeypot" aria-hidden="true"><label htmlFor="quote-check">Leave this field blank</label><input id="quote-check" name="quote_check" value={draft.company} onChange={(event) => updateField("company", event.target.value)} tabIndex={-1} autoComplete="new-password" /></div>
                </fieldset>
            {isSubmitting && <div className="upload-progress" role="status"><div><span>{files.length ? "Uploading attachments and sending quote" : "Sending quote request"}</span><strong>{uploadProgress || 1}%</strong></div><progress max="100" value={uploadProgress || 1}>{uploadProgress || 1}%</progress></div>}
            {notice && <p className={`form-notice${submissionId ? " form-notice--success" : ""}`} role="status" aria-live="polite">{submissionId && <strong>Request #{submissionId}. </strong>}{notice}</p>}
            <div className="form-submit"><div className="form-submit__details"><p>By submitting, you authorize Partition Stalls to contact you about this commercial project. We do not publish your information.</p><div ref={verificationRef} className="quote-form__verification" tabIndex={-1}><ShieldCheck aria-hidden="true" /><span>Quick security check runs automatically when you submit.</span><TurnstileWidget onVerify={handleVerifiedToken} onError={handleVerificationError} resetSignal={turnstileResetSignal} executeSignal={turnstileExecuteSignal} /></div></div><button type="submit" className="button button--primary" disabled={isSubmitting || isVerifying}>{isSubmitting ? "Submitting…" : isVerifying ? "Verifying…" : "Submit Quote Request"} {!isSubmitting && !isVerifying && <ArrowUpRight aria-hidden="true" />}</button></div>
              </form>
            </>
          )}
        </div>

        <aside className="quote-talk" aria-labelledby="quote-talk-title">
          <CallbackScheduler sourcePath="/request-a-quote" headingId="quote-talk-title" variant="quote" />
          <div className="quote-talk__links">
            <a href={`tel:${siteConfig.phoneHref}`}><Phone aria-hidden="true" /><span>Call</span><strong>{siteConfig.phoneDisplay}</strong></a>
            <a href={`mailto:${siteConfig.email}`}><Mail aria-hidden="true" /><span>Email</span><strong>{siteConfig.email}</strong></a>
          </div>
        </aside>
      </section>
    </>
  );
}
